import React from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Save, ChevronDown, Search, CornerDownRight } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/toast';
import { ACTION_REGISTRY, type ActionDef } from '@atlas/shared';

/** Tri-state scope for view / edit / delete on report rows. */
type Scope = 'all' | 'own' | 'none';

interface PermState {
  add: boolean;
  view: boolean; viewOwnRecord: boolean;
  edit: boolean; editOwnRecord: boolean;
  delete: boolean; deleteOwnRecord: boolean;
  actions: string[];
}

const defaultPerm = (): PermState => ({
  add: false,
  view: false, viewOwnRecord: false,
  edit: false, editOwnRecord: false,
  delete: false, deleteOwnRecord: false,
  actions: [],
});

function scopeOf(p: PermState, col: 'view' | 'edit' | 'delete'): Scope {
  const ownKey = `${col}OwnRecord` as const;
  if ((p as any)[col]) return 'all';
  if ((p as any)[ownKey]) return 'own';
  return 'none';
}

function setScope(p: PermState, col: 'view' | 'edit' | 'delete', scope: Scope): PermState {
  const ownKey = `${col}OwnRecord` as const;
  return {
    ...p,
    [col]: scope === 'all',
    [ownKey]: scope === 'own',
  } as PermState;
}

const TYPE_BADGE: Record<string, { label: string; cls: string }> = {
  form:   { label: 'Form',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200' },
  report: { label: 'Report', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200' },
  page:   { label: 'Page',   cls: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200' },
};

/** Resources hidden from the matrix entirely — entities present in the
 *  data model but not exposed to admins. */
const HIDDEN_SLUGS = new Set<string>([
  'all_customers',      // legacy report
]);

interface Resource {
  id: string;
  slug: string;
  name: string;
  type: 'form' | 'report' | 'page';
  sectionHeader: string | null;
  parentForm: string | null;
  sortOrder: number;
  /** False when the resource can't enforce an Own (record-scoped) grant —
   *  no owner field on its model. The matrix hides the "Own" option so admins
   *  aren't offered a setting that silently matches nothing (e.g. Assets,
   *  Service Plans). Server-derived; defaults true when unknown. */
  ownable?: boolean;
}

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: role, isLoading } = useQuery({
    queryKey: ['/roles', id],
    queryFn: async () => (await api<any>(`/roles/${id}`)).data,
    enabled: !!id,
  });

  const { data: resources = [] } = useQuery<Resource[]>({
    queryKey: ['/roles/_resources/all'],
    queryFn: async () => (await api<Resource[]>('/roles/_resources/all')).data || [],
  });

  const [perms, setPerms] = React.useState<Record<string, PermState>>({});
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!role) return;
    const byId: Record<string, PermState> = {};
    for (const p of role.permissions || []) {
      const rid = typeof p.resource === 'object' ? (p.resource._id || p.resource.id) : p.resource;
      byId[rid] = {
        add: !!p.add,
        view: !!p.view, viewOwnRecord: !!p.viewOwnRecord,
        edit: !!p.edit, editOwnRecord: !!p.editOwnRecord,
        delete: !!p.delete, deleteOwnRecord: !!p.deleteOwnRecord,
        actions: Array.isArray(p.actions) ? [...p.actions] : [],
      };
    }
    setPerms(byId);
  }, [role]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = Object.entries(perms).map(([resource, p]) => ({ resource, ...p }));
      const r = await api(`/roles/${id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: payload }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => {
      toast('Permissions saved', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/roles', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  function toggleAdd(rid: string) {
    setPerms((p) => ({ ...p, [rid]: { ...(p[rid] ?? defaultPerm()), add: !p[rid]?.add } }));
  }
  function togglePageView(rid: string) {
    setPerms((p) => ({ ...p, [rid]: { ...(p[rid] ?? defaultPerm()), view: !p[rid]?.view, viewOwnRecord: false } }));
  }
  function updateScope(rid: string, col: 'view' | 'edit' | 'delete', scope: Scope) {
    setPerms((p) => ({ ...p, [rid]: setScope(p[rid] ?? defaultPerm(), col, scope) }));
  }
  function grantAll(rid: string, type: 'form' | 'report' | 'page') {
    setPerms((p) => {
      const cur = p[rid] ?? defaultPerm();
      if (type === 'form') return { ...p, [rid]: { ...cur, add: true } };
      if (type === 'page') return { ...p, [rid]: { ...cur, view: true, viewOwnRecord: false } };
      // report — full access (add + view/edit/delete all, no Own)
      return {
        ...p,
        [rid]: {
          ...cur,
          add: true,
          view: true, viewOwnRecord: false,
          edit: true, editOwnRecord: false,
          delete: true, deleteOwnRecord: false,
        },
      };
    });
  }
  function revokeAll(rid: string) {
    setPerms((p) => ({ ...p, [rid]: { ...(p[rid] ?? defaultPerm()), ...defaultPerm() } }));
  }
  function toggleActionKey(rid: string, actionKey: string) {
    setPerms((p) => {
      const cur = p[rid] ?? defaultPerm();
      const has = cur.actions.includes(actionKey);
      const nextActions = has ? cur.actions.filter((k) => k !== actionKey) : [...cur.actions, actionKey];
      return { ...p, [rid]: { ...cur, actions: nextActions } };
    });
  }

  if (isLoading || !role) return <div className="p-8 text-muted-foreground">Loading…</div>;

  // ── Group resources for the matrix ──
  // Layout:
  //   for each section (Dashboard, People, Assets, …):
  //     for each form in the section, alphabetical:
  //       render the form row, then its child reports/pages indented under it
  //     then standalone reports/pages in that section (parentForm === null) alphabetical
  //
  // Form rows that have no children still render — admins can still grant Add.
  // Standalone pages (Dashboard, Settings, etc.) appear under their section
  // without a form parent.
  const visible = resources.filter((r) => !HIDDEN_SLUGS.has(r.slug));
  const matchesSearch = (r: Resource) => !search
    || r.name.toLowerCase().includes(search.toLowerCase())
    || (r.sectionHeader || '').toLowerCase().includes(search.toLowerCase());

  // First pass: index everything by slug + by parentForm
  const bySlug = new Map<string, Resource>();
  const childrenOfForm = new Map<string, Resource[]>();
  for (const r of visible) {
    bySlug.set(r.slug, r);
    if (r.parentForm && (r.type === 'report' || r.type === 'page')) {
      const arr = childrenOfForm.get(r.parentForm) ?? [];
      arr.push(r); childrenOfForm.set(r.parentForm, arr);
    }
  }
  for (const arr of childrenOfForm.values()) {
    arr.sort((a, b) => {
      // Reports before pages, then alpha.
      if (a.type !== b.type) return a.type === 'report' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }

  // Build a "row plan" per section: ordered list of (resource, indent) tuples.
  // A row is included if the row itself matches search OR (it's a form whose
  // children match) OR (it's a child whose parent matches) — so filtering by
  // a form name still shows the child cluster, and filtering by a child name
  // still includes its parent for context.
  const sections = new Map<string, Array<{ res: Resource; indent: boolean }>>();
  const usedAsChild = new Set<string>(
    [...childrenOfForm.values()].flat().map((c) => c.slug),
  );
  // Standalone pages/reports (no parentForm) render FIRST so navigation-only
  // gates like dashboards sit at the top of their section rather than being
  // buried beneath form clusters.
  const standalones = visible
    .filter((r) => r.type !== 'form'
      && !(r.parentForm && bySlug.has(r.parentForm))
      && !usedAsChild.has(r.slug)
      && matchesSearch(r))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  for (const r of standalones) {
    const sec = r.sectionHeader || 'Other';
    const rows = sections.get(sec) ?? [];
    rows.push({ res: r, indent: false });
    sections.set(sec, rows);
  }
  // Form clusters — append after standalone pages so admins see the
  // gate-style permission first, then the data CRUD beneath.
  for (const r of visible) {
    if (r.type !== 'form') continue;
    const children = childrenOfForm.get(r.slug) ?? [];
    const formMatches = matchesSearch(r);
    const childMatches = children.filter(matchesSearch);
    if (!formMatches && childMatches.length === 0) continue;
    const sec = r.sectionHeader || 'Other';
    const rows = sections.get(sec) ?? [];
    rows.push({ res: r, indent: false });
    const shownChildren = formMatches ? children : childMatches;
    for (const c of shownChildren) rows.push({ res: c, indent: true });
    sections.set(sec, rows);
  }

  // Visual order must mirror the sidebar's SIDEBAR_GROUPS so admins see the
  // matrix laid out the same way they navigate. Anything unmatched falls to
  // the bottom under "Other".
  const SECTION_ORDER = [
    'Dashboard',
    'Assets',
    'Servicing',
    'Compliance',
    'Job Cards',
    'Dispatches',
    'Admin',
    'Other',
  ];
  const orderIndex = (h: string) => {
    const i = SECTION_ORDER.indexOf(h);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const sortedSections = [...sections.entries()].sort((a, b) => {
    const oa = orderIndex(a[0]);
    const ob = orderIndex(b[0]);
    if (oa !== ob) return oa - ob;
    return a[0].localeCompare(b[0]);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild><Link to="/roles"><ChevronLeft className="h-4 w-4" /> Back</Link></Button>
      </div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{role.name} — Permissions</h1>
        <Button onClick={() => save.mutate()} disabled={save.isPending}><Save className="h-4 w-4" /> Save</Button>
      </div>

      <div className="space-y-1">
        <div className="relative max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Filter resources..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
        </div>
        <p className="text-xs text-muted-foreground">
          Forms control who can <strong>Add</strong> new records. Reports under each form control who can <strong>View</strong>, <strong>Edit</strong>, and <strong>Delete</strong> the records. Pages are simple navigation gates.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table wrapperClassName="relative w-full overflow-auto max-h-[calc(100vh-13rem)] rounded-b-xl">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky top-0 z-20 bg-background border-b">Resource</TableHead>
                <TableHead className="sticky top-0 z-20 bg-background border-b w-16 text-center">Add</TableHead>
                <TableHead className="sticky top-0 z-20 bg-background border-b w-28 text-center">View</TableHead>
                <TableHead className="sticky top-0 z-20 bg-background border-b w-28 text-center">Edit</TableHead>
                <TableHead className="sticky top-0 z-20 bg-background border-b w-28 text-center">Delete</TableHead>
                <TableHead className="sticky top-0 z-20 bg-background border-b w-16 text-center">Quick</TableHead>
                <TableHead className="sticky top-0 z-20 bg-background border-b min-w-[180px]">Custom actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSections.map(([sectionName, rows]) => (
                <React.Fragment key={sectionName}>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell colSpan={7} className="py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {sectionName}
                    </TableCell>
                  </TableRow>
                  {rows.map(({ res: r, indent }) => {
                    const p = perms[r.id] ?? defaultPerm();
                    const availableActions: ActionDef[] = ACTION_REGISTRY.filter((a: ActionDef) => a.resourceSlug === r.slug);
                    const grantedCount = p.actions.filter((k) => availableActions.some((a: ActionDef) => a.key === k)).length;
                    const badge = TYPE_BADGE[r.type] ?? TYPE_BADGE.page;
                    const isForm = r.type === 'form';
                    const isReport = r.type === 'report';
                    const isPage = r.type === 'page';
                    return (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className={`flex items-center gap-2 ${indent ? 'pl-6' : ''}`}>
                            {indent && <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />}
                            <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${badge.cls}`}>
                              {badge.label}
                            </span>
                            <span className="font-medium">{r.name}</span>
                          </div>
                        </TableCell>

                        {/* Add — forms and reports. Granting add on a
                            report surfaces the "+ New" button on that
                            report directly; granting add on the parent
                            form serves the same role and is honoured by
                            the server's permission bubbling. */}
                        <TableCell className="text-center">
                          {(isForm || isReport) ? (
                            <Checkbox checked={p.add} onCheckedChange={() => toggleAdd(r.id)} />
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </TableCell>

                        {/* View — pages use Yes/None checkbox; reports use scope select; forms blank */}
                        <TableCell className="text-center">
                          {isPage ? (
                            <Checkbox checked={p.view} onCheckedChange={() => togglePageView(r.id)} />
                          ) : isReport ? (
                            <ScopeSelect value={scopeOf(p, 'view')} onChange={(s) => updateScope(r.id, 'view', s)} allowOwn={r.ownable !== false} />
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Edit — reports only */}
                        <TableCell className="text-center">
                          {isReport ? (
                            <ScopeSelect value={scopeOf(p, 'edit')} onChange={(s) => updateScope(r.id, 'edit', s)} allowOwn={r.ownable !== false} />
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Delete — reports only */}
                        <TableCell className="text-center">
                          {isReport ? (
                            <ScopeSelect value={scopeOf(p, 'delete')} onChange={(s) => updateScope(r.id, 'delete', s)} allowOwn={r.ownable !== false} />
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Quick — grant-all / revoke-all */}
                        <TableCell className="text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 text-xs">…</Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => grantAll(r.id, r.type)}>
                                Grant all
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => revokeAll(r.id)} className="text-destructive">
                                Revoke all
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>

                        {/* Custom actions — reports only (per spec). Page-type
                            custom actions don't exist today; form actions are
                            triggered from a record context which is on the
                            report. */}
                        <TableCell>
                          {!isReport || availableActions.length === 0 ? (
                            <span className="text-xs text-muted-foreground/60">—</span>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="h-7 text-xs font-normal">
                                  {grantedCount}/{availableActions.length} granted
                                  <ChevronDown className="ml-1 h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[240px]">
                                {availableActions.map((a: ActionDef) => (
                                  <DropdownMenuItem
                                    key={a.key}
                                    onSelect={(e) => { e.preventDefault(); toggleActionKey(r.id, a.key); }}
                                    className="gap-2"
                                  >
                                    <Checkbox checked={p.actions.includes(a.key)} className="mr-1" />
                                    <span>{a.label}</span>
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ScopeSelect({ value, onChange, allowOwn = true }: { value: Scope; onChange: (s: Scope) => void; allowOwn?: boolean }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Scope)}>
      <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        {/* "Own" hidden for resources with no owner field — it can't be
            enforced there and would silently match nothing. */}
        {allowOwn && <SelectItem value="own">Own</SelectItem>}
        <SelectItem value="all">All</SelectItem>
      </SelectContent>
    </Select>
  );
}
